import { useState } from "react";
import { useNavigate } from "react-router";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./ui/use-toast";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

interface DeleteProfileProps {
  user: User;
}

export function DeleteProfile({ user }: DeleteProfileProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleDeleteProfile = async () => {
    if (deleteConfirmation !== "DELETE") {
      toast({
        title: t('menu.deleteProfile.error'),
        description: "Please type DELETE to confirm account deletion",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await supabase.functions.invoke('delete-profile', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });

      if (response.error || !response.data?.success) {
        console.error('Deletion failed:', response.error || 'No success in response');
        toast({
          title: t('menu.deleteProfile.error'),
          description: "We couldn't delete your account. Please try again or contact support if the problem persists.",
          variant: "destructive",
          duration: 5000,
        });
        setIsDeleting(false);
        return;
      }

      try {
        await supabase.auth.signOut({ scope: 'global' });

        toast({
          title: t('menu.deleteProfile.success'),
          description: t('menu.deleteProfile.success'),
          duration: 5000,
        });

        setIsDeleting(false);
        setIsOpen(false);

        // Small delay to ensure session is cleared before navigation
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 100);
      } catch (sessionError) {
        console.error('Error clearing session:', sessionError);
        // Even if session clearing fails, the account is deleted, so we should still redirect
        setIsDeleting(false);
        setIsOpen(false);
        navigate('/login', { replace: true });
      }
    } catch (error) {
      setIsDeleting(false);
      toast({
        title: t('menu.deleteProfile.error'),
        description: t('menu.deleteProfile.error'),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!isDeleting) {
          setIsOpen(open);
          if (!open) {
            setDeleteConfirmation("");
          }
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}>
          <Trash2 className="mr-2 h-4 w-4" />
          <span>{t('menu.deleteProfile.button')}</span>
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('menu.deleteProfile.title')}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>{t('menu.deleteProfile.description')}</p>
            <p>{t('menu.deleteProfile.warning')}</p>
            <p>
              {t('menu.deleteProfile.refund_before')}
              <a
                href="mailto:support@tuties.com"
              >
                support@tuties.com
              </a>
              {t('menu.deleteProfile.refund_after')}
            </p>
            <p>{t('menu.deleteProfile.subscriptionNote')}</p>
            <div className="space-y-2 pt-2">
              <Label htmlFor="delete-confirmation">
                Type <span className="font-mono font-bold">DELETE</span> to confirm:
              </Label>
              <Input
                id="delete-confirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="max-w-[200px]"
                disabled={isDeleting}
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('menu.deleteProfile.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteProfile();
            }}
            disabled={isDeleting || deleteConfirmation !== "DELETE"}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 relative"
          >
            {isDeleting ? (
              <>
                <span className="pl-6">Deleting Account...</span>
              </>
            ) : (
              t('menu.deleteProfile.confirm')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
